<template>
  <UserCommonLayout>
    <template #content>
      <section id="titleBanner">
        <div class="containerDash">
          <div class="titleBanner">
            <ul class="path flex">
              <li>{{ t('common-skillCertify') }}</li>
              <li>{{ t('common-takeAssessment') }}</li>
            </ul>
            <div class="bannerWrap flex">
              <div class="titleArea">
                <h2>{{ t('common-takeAssessment') }}</h2>
                <p>{{ t('skillCertify-take-assessment-description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="assessmentListWrap">
        <div class="containerDash">
          <div class="assessmentListWrap flex col gap-64">
            <LazyProSkillAssureAssessmentInviteds
                :myAssessmentPage="myAssessmentPage"
                :isSearchData="isHavingSearchData.length !== 0"
                @loadMore="onClickSeeMore"
            />
            <LazyProSkillAssureAssessmentSamples :sampleAssessmentList="sampleAssessments" />
          </div>
        </div>
      </section>
    </template>
  </UserCommonLayout>
</template>

<script setup lang="ts">
import {computed, useI18n, useRoute} from '#imports';
import {onMounted, ref, watch} from 'vue';
import {useUserStore} from '~~/application/store/user/userStore';
import {useLoadingStore} from '~/store/loading/loadingStore';
import {useProSkillAssureService} from '~/composables/service/useProSkillAssureService';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import {ProSkillAssureMyAssessmentModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureMyAssessmentModel';
import {
  ProSkillAssureSampleAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureSampleAssessmentModel';
import {useNuxtApp} from '#app';

// TSK-11215: no sessionOwner meta here on purpose. This is the invited/sample assessment LIST
// page — nothing is being taken and nothing is written, so there is no seat to protect. It also
// has no route param, so the tab-election scope would collapse to a constant and two tabs on the
// list would evict each other for no reason.

const {t} = useI18n();
const {$i18n} = useNuxtApp();
const route = useRoute();
const loadingStore = useLoadingStore();
const userStore = useUserStore();
const proSkillAssureService = useProSkillAssureService();

const lang = computed(() => $i18n.locale.value.toUpperCase());

const myAssessmentPage = ref<PageModel<ProSkillAssureMyAssessmentModel>>(new PageModel<ProSkillAssureMyAssessmentModel>());
const filters = ref<string[] | undefined>(['']);
const sampleAssessments = ref<ProSkillAssureSampleAssessmentModel[]>([]);

const isHavingSearchData = computed(() => route.query.filter ?? []);

// ASMT-CAND-16: 내 평가 페이지 — 초대/진행/완료(Completed) 평가 목록 + 샘플 평가
const init = (): Promise<void> =>
    Promise.all([
      proSkillAssureService.getMyAssessmentList(1, 9, undefined),
      proSkillAssureService.getSampleAssessmentList(lang.value, 1, 99999)
    ]).then(([myList, samples]) => {
      myAssessmentPage.value = myList;
      sampleAssessments.value = samples.data;
    }).finally(() => {
      loadingStore.closeLoading();
    }).then(() => undefined);

const filterUpperCase = (filter: string | string[]) =>
    typeof filter === 'string' ? filter.toUpperCase() : filter.map(d => d.toUpperCase());

const getMyAssessmentList = (moreFlag: boolean, page?: number, size?: number, filter?: string[]) => {
  const normalized = filter !== undefined ? (filterUpperCase(filter) as string[]) : undefined;
  proSkillAssureService.getMyAssessmentList(page, size, normalized).then(pageData => {
    myAssessmentPage.value = {
      ...pageData,
      data: moreFlag ? [...myAssessmentPage.value.data, ...pageData.data] : pageData.data
    };
  });
};

const onClickSeeMore = () => {
  getMyAssessmentList(true, myAssessmentPage.value.pageNumber + 1, 9);
};

onMounted(() => {
  init();
});

watch(() => route.query, () => {
  filters.value = route.query.filter as string[] | undefined;
  getMyAssessmentList(false, 1, 9, filters.value);
});
</script>
